import { useEffect, useState } from "react";
import { SectionContainer } from "@/components/shared";
import { trackEvent } from "@/lib/analytics/gtm";
import { websiteContent } from "../content";
import IndustryDetailPanel from "./IndustryDetailPanel";
import { usePanelViewTracking } from "../hooks/usePanelViewTracking";

const { industries } = websiteContent;

const scheduleIdleTask = (callback: () => void): number => {
  if (typeof window.requestIdleCallback === "function") {
    return window.requestIdleCallback(() => {
      callback();
    });
  }
  return window.setTimeout(callback, 1);
};

const cancelIdleTask = (taskId: number): void => {
  if (typeof window.cancelIdleCallback === "function") {
    window.cancelIdleCallback(taskId);
    return;
  }
  window.clearTimeout(taskId);
};

function IndustriesSection() {
  usePanelViewTracking({
    panelId: "industries",
    panelName: "Industries",
    panelIndex: 4,
  });

  useEffect(() => {
    let links: HTMLLinkElement[] = [];
    let idleId: number | null = null;
    let usedRequestIdle = false;

    if (typeof window !== "undefined") {
      const schedulePrefetch = () => {
        links = industries.groups.map((group) => {
          const link = document.createElement("link");
          link.rel = "prefetch";
          link.as = "image";
          link.href = group.image;
          document.head.appendChild(link);
          return link;
        });
      };

      if (
        "requestIdleCallback" in window &&
        typeof (window as any).requestIdleCallback === "function"
      ) {
        idleId = (window as any).requestIdleCallback(schedulePrefetch);
        usedRequestIdle = true;
      } else {
        idleId = window.setTimeout(schedulePrefetch, 0);
      }
    }

    return () => {
      if (typeof window !== "undefined" && idleId !== null) {
        if (
          usedRequestIdle &&
          "cancelIdleCallback" in window &&
          typeof (window as any).cancelIdleCallback === "function"
        ) {
          (window as any).cancelIdleCallback(idleId);
        } else {
          window.clearTimeout(idleId);
        }
      }
      links.forEach((link) => link.remove());
    };
  }, []);

  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const activeIndustry =
    activeIndex === null ? null : (industries.groups[activeIndex] ?? null);
  const openIndustry =
    openIndex === null ? null : (industries.groups[openIndex] ?? null);

  return (
    <SectionContainer
      id="industries"
      size="page"
      className="bg-brand-greenBright"
      innerClassName="relative min-h-[914px] !px-page-gutter md:min-h-[clamp(760px,100vh,1024px)] xl:!px-page-gutter"
    >
      <div
        className="relative min-h-[914px] overflow-hidden md:min-h-[clamp(760px,100vh,1024px)]"
        onMouseLeave={() => setActiveIndex(null)}
        onBlur={(event) => {
          if (
            !event.currentTarget.contains(event.relatedTarget as Node | null)
          ) {
            setActiveIndex(null);
          }
        }}
      >
        <div
          className={`absolute inset-0 bg-cover bg-center transition-opacity duration-300 ${
            activeIndustry ? "opacity-100" : "opacity-0"
          }`}
          style={
            activeIndustry
              ? { backgroundImage: `url(${activeIndustry.image})` }
              : undefined
          }
        />
        <div
          className={`absolute inset-0 bg-black/30 transition-opacity duration-300 ${
            activeIndustry ? "opacity-100" : "opacity-0"
          }`}
        />

        <div className="relative z-10 flex min-h-[914px] max-w-[330px] flex-col pt-[104px] md:min-h-[clamp(760px,100vh,1024px)] md:max-w-none md:pt-[clamp(56px,9vw,88px)]">
          <p
            className={`font-archivo text-[32px] font-extralight leading-[46px] transition-colors duration-300 md:text-[20px] md:leading-[16px] ${
              activeIndustry ? "text-gray-lightest" : "text-gray-dark"
            } md:uppercase`}
          >
            {industries.subtitle}
          </p>

          <div className="mt-[50px] flex flex-col gap-[21px] md:mt-[clamp(32px,6vw,56px)] md:gap-0">
            {industries.groups.map((group, index) => {
              const isActive = activeIndex === index;
              const isDimmed = activeIndex !== null && !isActive;
              return (
                <button
                  key={group.name}
                  type="button"
                  className={`relative flex h-[160px] w-full cursor-pointer items-center text-left transition-all duration-300 md:h-[clamp(112px,14vw,202px)] ${
                    isActive ? "text-gray-lightest" : "text-gray-dark"
                  } ${
                    isDimmed ? "opacity-0" : "opacity-100"
                  } group !bg-transparent outline-none ring-0 focus:outline-none focus:ring-0 appearance-none shadow-none`}
                  onMouseEnter={() => setActiveIndex(index)}
                  onFocus={() => setActiveIndex(index)}
                  onClick={() => {
                    setActiveIndex(index);
                    setOpenIndex(index);
                    trackEvent("industry_detail_open", {
                      industry_name: group.name,
                    });
                  }}
                >
                  <div
                    className={`absolute inset-0 transition-opacity duration-300 ${
                      isActive ? "opacity-100" : "opacity-0"
                    }`}
                  >
                    <div className="absolute inset-0 bg-[rgba(20,20,20,0.4)] backdrop-blur-[12px]" />
                  </div>

                  <span className="relative z-10 block w-full max-w-[306px] pr-0 font-agency font-black text-[90px] leading-[80px] tracking-[0.9px] uppercase md:max-w-none md:text-[clamp(2rem,8vw,11.25rem)] md:leading-[clamp(2.2rem,7.2vw,11.25rem)] md:pr-[clamp(56px,9vw,120px)] md:tracking-[1.8px]">
                    {group.name.toUpperCase()}
                  </span>

                  {isActive ? (
                    <span className="absolute top-1/2 z-10 hidden size-[clamp(48px,6vw,64px)] -translate-y-1/2 items-center justify-center rounded-full bg-brand-greenBright text-brand-green transition-transform duration-200 group-hover:scale-[1.05] group-focus-visible:scale-[1.05] md:right-[clamp(12px,2vw,24px)] md:flex">
                      <svg
                        aria-hidden="true"
                        viewBox="0 0 29.1667 29.1667"
                        className="size-[clamp(22px,2.4vw,30px)]"
                        fill="none"
                      >
                        <path
                          d="M18.2609 1.25H27.9167M27.9167 1.25V10.9052M27.9167 1.25L16.6516 12.5144M10.9058 27.9167H1.25M1.25 27.9167V18.2615M1.25 27.9167L12.5151 16.6523"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <IndustryDetailPanel
        industry={openIndustry}
        isOpen={openIndex !== null}
        onClose={() => {
          setOpenIndex(null);
          setActiveIndex(null);
        }}
      />
    </SectionContainer>
  );
}

export default IndustriesSection;
